import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency, formatDate, formatFechaLimite } from '../../services/api';
import { rentabilidadGlobalAPI } from '../../modules/administracion/services/rentabilidadGlobalAPI';
import { buildAnalisisMaestra } from './analisisMaestraUtils';
import { generarInformeNarrativo } from './generarInformeNarrativo';
import PanelGraficosAnalisis from './PanelGraficosAnalisis';

const styles = `
  .tma-root--fullscreen { display: flex; flex-direction: column; height: 100%; min-height: 0; background: #f8fafc; }
  .tma-scroll { flex: 1; min-height: 0; overflow: auto; background: #fff; }
  .tma-root--fullscreen .tma-scroll { border-top: 1px solid #e2e8f0; }

  .tma-header {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
    padding: 14px 18px; border-bottom: 1px solid #e2e8f0; background: #fff; flex-shrink: 0; flex-wrap: wrap;
  }
  .tma-header-title { font-size: 1.05rem; font-weight: 700; color: #0f172a; margin: 0; }
  .tma-header-sub { font-size: 0.76rem; color: #64748b; margin: 4px 0 0; max-width: 520px; line-height: 1.45; }

  .tma-mode-bar {
    display: flex; gap: 6px; padding: 10px 18px; background: #f8fafc;
    border-bottom: 1px solid #e2e8f0; flex-shrink: 0; flex-wrap: wrap; align-items: center;
  }
  .tma-mode-btn, .tma-subtab {
    border: 1px solid #e2e8f0; background: #fff; color: #475569;
    border-radius: 8px; padding: 7px 12px; font-size: 0.76rem; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  }
  .tma-mode-btn:hover, .tma-subtab:hover { border-color: #c7d2fe; color: #4338ca; }
  .tma-mode-btn--active { background: #4f46e5; border-color: #4f46e5; color: #fff; }
  .tma-subtab--active { background: #eef2ff; border-color: #a5b4fc; color: #3730a3; }
  .tma-mode-btn--ghost { background: transparent; border-style: dashed; color: #64748b; }

  .tma-insights {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px; padding: 14px 18px; background: #fafbff; border-bottom: 1px solid #e2e8f0;
  }
  .tma-insight {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px;
  }
  .tma-insight-label {
    font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    color: #6366f1; margin-bottom: 6px;
  }
  .tma-insight-value { font-size: 0.88rem; font-weight: 700; color: #0f172a; line-height: 1.35; }
  .tma-insight-detail { font-size: 0.72rem; color: #64748b; margin-top: 4px; }

  .tma-table {
    width: 100%; border-collapse: separate; border-spacing: 0;
    font-size: 0.76rem; color: #334155;
  }
  .tma-table thead th {
    position: sticky; top: 0; z-index: 2; background: #fff;
    border-bottom: 1px solid #e2e8f0; padding: 8px 12px;
    font-weight: 600; font-size: 0.68rem; letter-spacing: 0.02em;
    text-transform: uppercase; color: #64748b; white-space: nowrap;
  }
  .tma-table tbody td {
    padding: 7px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; line-height: 1.35;
  }
  .tma-table tbody tr:hover td { background: #f8fafc; }
  .tma-table tbody tr:nth-child(even) td { background: #fcfdfe; }
  .tma-table tbody tr:nth-child(even):hover td { background: #f1f5f9; }

  .tma-rank {
    display: inline-flex; align-items: center; justify-content: center;
    width: 22px; height: 22px; border-radius: 6px; font-size: 0.68rem; font-weight: 800;
    background: #eef2ff; color: #4338ca;
  }
  .tma-rank--1 { background: #fef3c7; color: #b45309; }
  .tma-rank--2 { background: #f1f5f9; color: #475569; }
  .tma-rank--3 { background: #ffedd5; color: #c2410c; }

  .tma-code { font-weight: 700; color: #0f172a; white-space: nowrap; }
  .tma-muted { color: #94a3b8; }
  .tma-money { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .tma-money--ok { color: #059669; }
  .tma-money--due { color: #dc2626; }
  .tma-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; display: block; }

  .tma-pill {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    font-size: 0.65rem; font-weight: 700; letter-spacing: 0.02em; line-height: 1.4;
  }
  .tma-pill--paid { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .tma-pill--pending { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  .tma-pill--ok { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .tma-pill--warn { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
  .tma-pill--danger { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

  .tma-interp { font-size: 0.72rem; color: #475569; font-style: italic; }
  .tma-section-title {
    padding: 12px 18px 6px; font-size: 0.72rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.04em; color: #64748b;
  }

  .tma-search input {
    border: 1px solid #e2e8f0; border-radius: 8px; padding: 7px 10px;
    font-size: 0.8rem; min-width: 200px;
  }
  .tma-btn-close {
    border: 1px solid #e2e8f0; background: #fff; color: #475569;
    border-radius: 8px; padding: 7px 12px; font-size: 0.78rem; font-weight: 600; cursor: pointer;
  }
  .tma-btn-close:hover { background: #f8fafc; }

  .tma-input {
    border: 1px solid transparent; background: transparent; border-radius: 6px;
    padding: 3px 6px; font-size: 0.72rem; width: 100%;
  }
  .tma-input:focus { outline: none; border-color: #818cf8; background: #fff; }
  .tma-note { padding: 10px 18px; font-size: 0.72rem; color: #64748b; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }

  .tma-insight-metric {
    display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;
  }
  .tma-chip {
    font-size: 0.68rem; font-weight: 600; padding: 3px 8px; border-radius: 6px;
    background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
  }
  .tma-chip--accent { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
  .tma-chip--danger { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .tma-chip--ok { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }

  .tma-guia {
    margin: 0 18px 12px; padding: 12px 14px; border-radius: 10px;
    background: #fffbeb; border: 1px solid #fde68a; font-size: 0.72rem; color: #78350f; line-height: 1.5;
  }
  .tma-guia strong { color: #92400e; }
  .tma-guia ul { margin: 6px 0 0; padding-left: 18px; }

  .tma-nav-back {
    border: none; background: transparent; color: #6366f1; font-size: 0.76rem;
    font-weight: 600; cursor: pointer; padding: 7px 10px; display: inline-flex; align-items: center; gap: 6px;
  }
  .tma-nav-back:hover { color: #4338ca; }

  .tma-graficos { padding: 14px 18px; background: #fafbff; border-bottom: 1px solid #e2e8f0; }
  .tma-graficos-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; margin-bottom: 12px; }
  .tma-grafico-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
  .tma-grafico-card--wide { grid-column: 1 / -1; }
  .tma-grafico-titulo { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; margin-bottom: 10px; }
  .tma-donut-bar { display: flex; height: 10px; border-radius: 999px; overflow: hidden; background: #f1f5f9; }
  .tma-donut-seg--ok { background: #10b981; }
  .tma-donut-seg--due { background: #ef4444; }
  .tma-donut-seg--accent { background: #6366f1; }
  .tma-donut-seg--muted { background: #cbd5e1; }
  .tma-donut-labels { display: flex; gap: 14px; margin-top: 8px; font-size: 0.72rem; color: #475569; flex-wrap: wrap; }
  .tma-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
  .tma-dot--ok { background: #10b981; }
  .tma-dot--due { background: #ef4444; }
  .tma-dot--accent { background: #6366f1; }
  .tma-dot--muted { background: #cbd5e1; }
  .tma-grafico-alerta { margin-top: 8px; font-size: 0.72rem; color: #b91c1c; font-weight: 600; }
  .tma-bars { display: flex; flex-direction: column; gap: 8px; }
  .tma-bar-row { display: grid; grid-template-columns: 140px 1fr 90px 72px; gap: 8px; align-items: center; font-size: 0.72rem; }
  .tma-bar-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #334155; font-weight: 600; }
  .tma-bar-track { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
  .tma-bar-fill { height: 100%; background: linear-gradient(90deg, #6366f1, #818cf8); border-radius: 999px; }
  .tma-bar-val { text-align: right; font-variant-numeric: tabular-nums; color: #0f172a; }
  .tma-bar-pct { text-align: right; color: #64748b; font-size: 0.68rem; }

  .informe-documento { max-width: 680px; margin: 0 auto; padding: 32px 28px 48px; }
  .informe-doc-kicker { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6366f1; }
  .informe-doc-title { font-size: 1.75rem; font-weight: 800; color: #0f172a; margin: 8px 0 6px; line-height: 1.2; text-transform: capitalize; }
  .informe-doc-meta { font-size: 0.78rem; color: #94a3b8; margin: 0; }

  .informe-hero-kpis {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
    margin: 28px 0 32px; padding: 20px 0; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9;
  }
  .informe-hero-kpi { text-align: center; }
  .informe-hero-kpi-val { display: block; font-size: 1.35rem; font-weight: 800; color: #0f172a; letter-spacing: -0.02em; }
  .informe-hero-kpi-val--ok { color: #047857; }
  .informe-hero-kpi-val--due { color: #b91c1c; }
  .informe-hero-kpi-lbl { font-size: 0.72rem; color: #94a3b8; margin-top: 4px; display: block; }

  .informe-veredicto { margin-bottom: 28px; }
  .informe-veredicto-texto { font-size: 1.05rem; line-height: 1.7; color: #334155; margin: 0; }

  .informe-pulse-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 32px; }
  .informe-pulse {
    display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px;
    border-radius: 10px; border: 1px solid #e2e8f0; background: #fff;
  }
  .informe-pulse i { font-size: 0.9rem; margin-top: 2px; color: #94a3b8; }
  .informe-pulse--ok { border-color: #bbf7d0; background: #f0fdf4; }
  .informe-pulse--ok i { color: #059669; }
  .informe-pulse--warning { border-color: #fde68a; background: #fffbeb; }
  .informe-pulse--warning i { color: #d97706; }
  .informe-pulse--danger { border-color: #fecaca; background: #fef2f2; }
  .informe-pulse--danger i { color: #dc2626; }
  .informe-pulse-titulo { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; }
  .informe-pulse-detalle { display: block; font-size: 0.82rem; font-weight: 600; color: #1e293b; margin-top: 2px; }

  .informe-bloque { margin-top: 32px; }
  .informe-bloque--alerta { background: #fff; border: 1px solid #fecaca; border-radius: 12px; padding: 18px 20px; }
  .informe-bloque--suave { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 20px; }
  .informe-bloque--conclusion { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; padding: 18px 20px; margin-top: 36px; }
  .informe-bloque-titulo { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin: 0 0 14px; }

  .informe-atencion-lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
  .informe-atencion-item { display: flex; gap: 12px; align-items: flex-start; }
  .informe-atencion-icon {
    width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .informe-atencion-icon--cobranza { background: #fef2f2; color: #dc2626; }
  .informe-atencion-icon--plazo { background: #fff7ed; color: #ea580c; }
  .informe-atencion-body { flex: 1; min-width: 0; }
  .informe-atencion-cabecera { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 10px; }
  .informe-atencion-cabecera strong { font-size: 0.88rem; color: #0f172a; }
  .informe-atencion-cliente { font-size: 0.8rem; color: #64748b; }
  .informe-atencion-monto { font-size: 0.88rem; font-weight: 700; color: #b91c1c; margin-left: auto; }
  .informe-atencion-detalle { font-size: 0.82rem; color: #475569; margin: 4px 0 0; line-height: 1.45; }

  .informe-enorden-lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .informe-enorden-lista li {
    display: grid; grid-template-columns: auto 1fr auto; gap: 4px 10px; align-items: baseline;
    font-size: 0.8rem; color: #64748b; padding-bottom: 8px; border-bottom: 1px solid #f1f5f9;
  }
  .informe-enorden-lista li:last-child { border-bottom: none; padding-bottom: 0; }
  .informe-enorden-lista strong { color: #334155; font-size: 0.82rem; }
  .informe-enorden-saldo { font-weight: 700; color: #475569; font-variant-numeric: tabular-nums; }
  .informe-enorden-detalle { grid-column: 1 / -1; font-size: 0.76rem; color: #94a3b8; }

  .informe-destacados { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .informe-destacados li { font-size: 0.88rem; color: #475569; padding: 10px 14px; background: #f8fafc; border-radius: 8px; }

  .informe-acciones { margin: 0; padding-left: 22px; font-size: 0.92rem; line-height: 1.65; color: #14532d; }
  .informe-acciones li { margin-bottom: 10px; }
  .informe-acciones li:last-child { margin-bottom: 0; }

  .informe-footer { margin-top: 40px; padding-top: 28px; border-top: 1px solid #f1f5f9; text-align: center; }
  .informe-btn-analisis {
    border: none; background: linear-gradient(135deg, #4f46e5, #6366f1); color: #fff;
    border-radius: 12px; padding: 14px 28px; font-size: 0.92rem; font-weight: 700; cursor: pointer;
    display: inline-flex; flex-direction: column; align-items: center; gap: 4px;
    box-shadow: 0 6px 20px rgba(99,102,241,0.28);
  }
  .informe-btn-analisis:hover { filter: brightness(1.05); }
  .informe-btn-hint { font-size: 0.7rem; font-weight: 500; opacity: 0.8; }
`;

const zonaLabel = (exp) => {
  const partes = [exp.distrito, exp.sector].filter(Boolean);
  return partes.length ? partes.join(' / ') : '—';
};

const fmtRetrasoDestacado = (exp) => {
  if (!exp) return null;
  if (exp.diasRetraso > 0) {
    return { texto: `+${exp.diasRetraso} días`, sub: exp.etiquetaDemora, tono: 'danger' };
  }
  if (exp.diasRetraso < 0) {
    return { texto: `${Math.abs(exp.diasRetraso)} d antes`, sub: exp.etiquetaDemora, tono: 'ok' };
  }
  return { texto: 'Sin retraso', sub: exp.etiquetaDemora, tono: 'ok' };
};

const fmtRentableDestacado = (exp) => {
  if (!exp) return null;
  const d = exp.desgloseRentabilidad || {};
  return {
    vendido: exp.montoAPagar,
    precioM2: exp.precioPorM2,
    pctCobrado: exp.pctCobrado,
    score: exp.scoreRentabilidad,
    desglose: d
  };
};

const DemoraPill = ({ tono, label }) => {
  const cls = tono === 'danger' ? 'tma-pill--danger' : tono === 'warning' ? 'tma-pill--warn' : 'tma-pill--ok';
  return <span className={`tma-pill ${cls}`}>{label}</span>;
};

const PanelAnalisis = ({ analisis, subTab }) => {
  const { rentabilidadExpedientes, demorasExpedientes, rentabilidadPorServicio, clientesRanking } = analisis;

  if (subTab === 'rentabilidad') {
    return (
      <>
        <div className="tma-note">
          Rentabilidad estimada sin costos internos: combina precio/m², monto vendido y % cobrado.
        </div>
        <div className="tma-section-title">Top expedientes más rentables</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>#</th><th>Expediente</th><th>Cliente</th><th>Servicio</th>
              <th>Precio/m²</th><th>Vendido</th><th>% Cobrado</th><th>Score</th><th>Qué significa</th>
            </tr>
          </thead>
          <tbody>
            {rentabilidadExpedientes.slice(0, 15).map((exp, i) => (
              <tr key={exp.id}>
                <td><span className={`tma-rank${i < 3 ? ` tma-rank--${i + 1}` : ''}`}>{i + 1}</span></td>
                <td><span className="tma-code">{exp.numero}</span></td>
                <td><span className="tma-truncate" title={exp.cliente.nombre}>{exp.cliente.nombre}</span></td>
                <td><span className="tma-truncate" title={exp.tipo.nombre}>{exp.tipo.nombre}</span></td>
                <td className="tma-money">{exp.precioPorM2 != null ? formatCurrency(exp.precioPorM2) : <span className="tma-muted">sin m²</span>}</td>
                <td className="tma-money">{formatCurrency(exp.montoAPagar)}</td>
                <td>{exp.pctCobrado}%</td>
                <td>
                  <strong>{exp.scoreRentabilidad}</strong>
                  {exp.desgloseRentabilidad && (
                    <div className="tma-muted" style={{ fontSize: '0.65rem' }}>
                      ({exp.desgloseRentabilidad.precioM2}+{exp.desgloseRentabilidad.cobranza}+{exp.desgloseRentabilidad.monto})
                    </div>
                  )}
                </td>
                <td><span className="tma-interp">{exp.interpretacionRentabilidad}</span></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="tma-section-title">Por tipo de servicio</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Servicio</th><th>Exp.</th><th>Ingreso vendido</th><th>% Cobrado</th>
              <th>Precio/m² prom.</th><th>Score prom.</th>
            </tr>
          </thead>
          <tbody>
            {rentabilidadPorServicio.map((g) => (
              <tr key={g.tipo}>
                <td><strong>{g.tipo}</strong></td>
                <td>{g.cantidad}</td>
                <td className="tma-money">{formatCurrency(g.vendido)}</td>
                <td>{g.pctCobrado}%</td>
                <td className="tma-money">{g.precioM2Prom != null ? formatCurrency(g.precioM2Prom) : '—'}</td>
                <td><strong>{g.scoreProm}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (subTab === 'demoras') {
    return (
      <>
        <div className="tma-note">
          Demora = días después de la fecha pactada. Los vencidos sin entregar aparecen primero.
        </div>
        <div className="tma-section-title">Expedientes con mayor demora</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Expediente</th><th>Cliente</th><th>Plazo pactado</th><th>Días en obra</th>
              <th>Retraso</th><th>Situación</th>
            </tr>
          </thead>
          <tbody>
            {demorasExpedientes.slice(0, 20).map((exp) => (
              <tr key={exp.id}>
                <td><span className="tma-code">{exp.numero}</span></td>
                <td><span className="tma-truncate" title={exp.cliente.nombre}>{exp.cliente.nombre}</span></td>
                <td>{exp.diasPrometidos} d</td>
                <td>{exp.diasTranscurridos} d</td>
                <td>{exp.diasRetraso > 0 ? `+${exp.diasRetraso} d` : exp.diasRetraso < 0 ? `${exp.diasRetraso} d` : '—'}</td>
                <td><DemoraPill tono={exp.tonoDemora} label={exp.etiquetaDemora} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="tma-section-title">Demoras por tipo de servicio</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Servicio</th><th>Exp.</th><th>% Vencidos</th><th>Retraso prom. (d)</th>
            </tr>
          </thead>
          <tbody>
            {rentabilidadPorServicio
              .slice()
              .sort((a, b) => b.pctVencidos - a.pctVencidos || b.retrasoProm - a.retrasoProm)
              .map((g) => (
                <tr key={g.tipo}>
                  <td><strong>{g.tipo}</strong></td>
                  <td>{g.cantidad}</td>
                  <td className={g.pctVencidos > 30 ? 'tma-money--due' : ''}>{g.pctVencidos}%</td>
                  <td>{g.retrasoProm || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </>
    );
  }

  if (subTab === 'cobranza') {
    const conAlerta = (analisis.datosEnriquecidos || []).filter(
      (e) => (e.adeudado > 0) || e.alertaCobranza
    );
    return (
      <>
        <div className="tma-note">
          Expedientes con saldo pendiente o cobranza fuera de ritmo respecto al plazo.
        </div>
        <div className="tma-section-title">Seguimiento de cobranza</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Expediente</th><th>Cliente</th><th>Vendido</th><th>Cobrado</th>
              <th>Saldo</th><th>%</th><th>Estado cobranza</th>
            </tr>
          </thead>
          <tbody>
            {conAlerta.slice(0, 30).map((e) => (
              <tr key={e.id}>
                <td><span className="tma-code">{e.numero}</span></td>
                <td><span className="tma-truncate">{e.cliente.nombre}</span></td>
                <td className="tma-money">{formatCurrency(e.montoAPagar)}</td>
                <td className="tma-money tma-money--ok">{formatCurrency(e.totalAbonado)}</td>
                <td className="tma-money tma-money--due">{formatCurrency(e.adeudado)}</td>
                <td>{e.pctCobrado}%</td>
                <td>{e.etiquetaCobranza || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (subTab === 'servicios') {
    return (
      <>
        <div className="tma-note">
          Desempeño agregado por tipo de servicio en el periodo seleccionado.
        </div>
        <div className="tma-section-title">Rentabilidad por servicio</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Servicio</th><th>Exp.</th><th>Vendido</th><th>Cobrado</th>
              <th>% Cobrado</th><th>Score prom.</th><th>% Vencidos</th><th>Retraso prom.</th>
            </tr>
          </thead>
          <tbody>
            {rentabilidadPorServicio.map((g) => (
              <tr key={g.tipo}>
                <td><strong>{g.tipo}</strong></td>
                <td>{g.cantidad}</td>
                <td className="tma-money">{formatCurrency(g.vendido)}</td>
                <td className="tma-money">{formatCurrency(g.cobrado)}</td>
                <td>{g.pctCobrado}%</td>
                <td><strong>{g.scoreProm}</strong></td>
                <td>{g.pctVencidos}%</td>
                <td>{g.retrasoProm || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (subTab === 'rentabilidad-real') {
    const lista = (analisis.datosEnriquecidos || [])
      .filter((e) => e.margenReal != null || e.costoTotal > 0)
      .sort((a, b) => (b.utilidadReal || 0) - (a.utilidadReal || 0));
    return (
      <>
        <div className="tma-note">
          Utilidad y margen con costo MO, costos directos y costo fijo asignado.
        </div>
        <div className="tma-section-title">Rentabilidad real por expediente</div>
        <table className="tma-table">
          <thead>
            <tr>
              <th>Expediente</th><th>Cliente</th><th>Servicio</th><th>Vendido</th>
              <th>Costo total</th><th>Utilidad</th><th>Margen</th><th>Horas</th><th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {lista.slice(0, 25).map((e) => (
              <tr key={e.id}>
                <td><span className="tma-code">{e.numero}</span></td>
                <td><span className="tma-truncate">{e.cliente.nombre}</span></td>
                <td><span className="tma-truncate">{e.tipo.nombre}</span></td>
                <td className="tma-money">{formatCurrency(e.montoAPagar)}</td>
                <td className="tma-money">{formatCurrency(e.costoTotal || 0)}</td>
                <td className="tma-money" style={{ color: (e.utilidadReal || 0) >= 0 ? '#047857' : '#b91c1c' }}>
                  {formatCurrency(e.utilidadReal || 0)}
                </td>
                <td>{e.margenReal != null ? `${e.margenReal}%` : '—'}</td>
                <td>{e.horasTotales ?? '—'}</td>
                <td>{e.estadoEconomico?.etiqueta || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <>
      <div className="tma-note">
        Valor del cliente: recurrencia, buena cobranza, bajo saldo pendiente y pocas demoras.
      </div>
      <div className="tma-section-title">Ranking de clientes</div>
      <table className="tma-table">
        <thead>
          <tr>
            <th>#</th><th>Cliente</th><th>Exp.</th><th>Vendido</th><th>Cobrado</th>
            <th>Saldo</th><th>% Cobrado</th><th>Retraso prom.</th><th>Valor</th><th>Qué significa</th>
          </tr>
        </thead>
        <tbody>
          {clientesRanking.slice(0, 20).map((c, i) => (
            <tr key={c.cliente.id}>
              <td><span className={`tma-rank${i < 3 ? ` tma-rank--${i + 1}` : ''}`}>{i + 1}</span></td>
              <td><span className="tma-truncate" title={c.cliente.nombre}>{c.cliente.nombre}</span></td>
              <td>{c.cantidad}</td>
              <td className="tma-money">{formatCurrency(c.vendido)}</td>
              <td className="tma-money tma-money--ok">{formatCurrency(c.cobrado)}</td>
              <td className="tma-money tma-money--due">{formatCurrency(c.saldo)}</td>
              <td>{c.pctCobrado}%</td>
              <td>{c.retrasoProm ? `${c.retrasoProm} d` : '—'}</td>
              <td><strong>{c.scoreValor}</strong></td>
              <td><span className="tma-interp">{c.interpretacionCliente}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const TablaBrutaAdmin = ({ datos, totales, onExpedienteActualizado, busqueda, setBusqueda }) => {
  const [ordenamiento] = useState({ campo: 'numero', orden: 'desc' });
  const [guardandoId, setGuardandoId] = useState(null);
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [edicion, setEdicion] = useState({});

  const datosOrdenados = useMemo(() => {
    if (!datos) return [];
    let resultado = [...datos];
    if (busqueda) {
      const t = busqueda.toLowerCase();
      resultado = resultado.filter((exp) =>
        exp.numero.toLowerCase().includes(t) ||
        exp.cliente.nombre.toLowerCase().includes(t) ||
        exp.tipo.nombre.toLowerCase().includes(t) ||
        (exp.observacionesAdmin || '').toLowerCase().includes(t)
      );
    }
    resultado.sort((a, b) => {
      let va = a[ordenamiento.campo];
      let vb = b[ordenamiento.campo];
      if (ordenamiento.campo === 'cliente') { va = a.cliente.nombre; vb = b.cliente.nombre; }
      if (typeof va === 'string') {
        return ordenamiento.orden === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return ordenamiento.orden === 'asc' ? va - vb : vb - va;
    });
    return resultado;
  }, [datos, ordenamiento, busqueda]);

  const guardarCampo = useCallback(async (exp, campo) => {
    const key = `${exp.id}:${campo}`;
    if (edicion[key] === undefined) return;
    const valor = edicion[key];
    const payload = campo === 'numeroPisos'
      ? { numeroPisos: valor === '' ? null : parseInt(valor, 10) }
      : { observacionesAdmin: valor };

    setGuardandoId(exp.id);
    try {
      const res = await rentabilidadGlobalAPI.patchExpedienteAdmin(exp.id, payload);
      onExpedienteActualizado?.(res.data);
      setEdicion((p) => { const n = { ...p }; delete n[key]; return n; });
    } catch (err) {
      setErrorGuardado(err.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardandoId(null);
    }
  }, [edicion, onExpedienteActualizado]);

  const val = (id, campo, fb) => {
    const k = `${id}:${campo}`;
    return edicion[k] !== undefined ? edicion[k] : fb;
  };

  return (
    <>
      {errorGuardado && (
        <div style={{ padding: '8px 18px', background: '#fef2f2', color: '#b91c1c', fontSize: '0.78rem' }}>
          {errorGuardado}
        </div>
      )}
      <div className="tma-note">
        Datos brutos sin procesar. Usa esta vista solo para editar pisos, notas admin o exportar detalle.
      </div>
      <table className="tma-table">
        <thead>
          <tr>
            {['Código', 'Cliente', 'Servicio', 'm²', 'Pisos', 'Zona', 'Inicio', 'Pactada', 'Real', 'Pago', 'Vendido', 'Cobrado', 'Saldo', 'S/m²', 'Obs. admin'].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {datosOrdenados.map((exp) => (
            <tr key={exp.id} style={{ opacity: guardandoId === exp.id ? 0.5 : 1 }}>
              <td><span className="tma-code">{exp.numero}</span></td>
              <td><span className="tma-truncate">{exp.cliente.nombre}</span></td>
              <td><span className="tma-truncate">{exp.tipo.nombre}</span></td>
              <td>{exp.metrosCuadrados ?? '—'}</td>
              <td>
                <input className="tma-input" type="number" min="0" max="99" style={{ width: 48 }}
                  value={val(exp.id, 'numeroPisos', exp.numeroPisos ?? '')}
                  onChange={(e) => setEdicion((p) => ({ ...p, [`${exp.id}:numeroPisos`]: e.target.value }))}
                  onBlur={() => guardarCampo(exp, 'numeroPisos')}
                />
              </td>
              <td>{zonaLabel(exp)}</td>
              <td>{formatDate(exp.fechaCreacion)}</td>
              <td>{formatFechaLimite(exp.fechaLimite)}</td>
              <td>{exp.fechaEntregaReal ? formatFechaLimite(exp.fechaEntregaReal) : '—'}</td>
              <td>
                <span className={`tma-pill ${exp.estadoPagoAdmin === 'PAGADO' ? 'tma-pill--paid' : 'tma-pill--pending'}`}>
                  {exp.estadoPagoAdmin === 'PAGADO' ? 'Pagado' : 'Pendiente'}
                </span>
              </td>
              <td className="tma-money">{formatCurrency(exp.montoAPagar)}</td>
              <td className="tma-money tma-money--ok">{formatCurrency(exp.totalAbonado)}</td>
              <td className="tma-money tma-money--due">{formatCurrency(exp.adeudado)}</td>
              <td className="tma-money">{exp.precioPorM2 != null ? formatCurrency(exp.precioPorM2) : '—'}</td>
              <td>
                <input className="tma-input" type="text" placeholder="Nota..."
                  value={val(exp.id, 'observacionesAdmin', exp.observacionesAdmin || '')}
                  onChange={(e) => setEdicion((p) => ({ ...p, [`${exp.id}:observacionesAdmin`]: e.target.value }))}
                  onBlur={() => guardarCampo(exp, 'observacionesAdmin')}
                />
              </td>
            </tr>
          ))}
        </tbody>
        {totales && datosOrdenados.length > 0 && (
          <tfoot>
            <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
              <td colSpan={10}>Totales ({datosOrdenados.length})</td>
              <td className="tma-money">{formatCurrency(datosOrdenados.reduce((s, e) => s + e.montoAPagar, 0))}</td>
              <td className="tma-money tma-money--ok">{formatCurrency(totales.totalAbonado)}</td>
              <td className="tma-money tma-money--due">{formatCurrency(totales.totalAdeudado)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
    </>
  );
};

const VistaMaestraContenido = ({
  datos,
  totales,
  filtros,
  onExpedienteActualizado,
  onClose,
  initialNivel = 'analisis',
  initialSubTab = 'rentabilidad',
  onSubTabChange,
  hideClose = false,
}) => {
  const [nivel, setNivel] = useState(initialNivel);
  const [subTab, setSubTab] = useState(initialSubTab);

  const cambiarSubTab = useCallback((id) => {
    setSubTab(id);
    onSubTabChange?.(id);
  }, [onSubTabChange]);

  useEffect(() => {
    setSubTab(initialSubTab);
  }, [initialSubTab]);
  const [busqueda, setBusqueda] = useState('');

  const datosFiltrados = useMemo(() => {
    if (!busqueda) return datos || [];
    const t = busqueda.toLowerCase();
    return (datos || []).filter((exp) =>
      exp.numero.toLowerCase().includes(t) ||
      exp.cliente.nombre.toLowerCase().includes(t) ||
      exp.tipo.nombre.toLowerCase().includes(t) ||
      (exp.distrito || '').toLowerCase().includes(t)
    );
  }, [datos, busqueda]);

  const analisis = useMemo(() => buildAnalisisMaestra(datosFiltrados), [datosFiltrados]);
  const informe = useMemo(
    () => generarInformeNarrativo(analisis, filtros),
    [analisis, filtros]
  );
  const { resumen } = analisis;

  const titulos = {
    analisis: 'Análisis detallado',
    bruto: 'Datos en bruto'
  };

  return (
    <div className="tma-root tma-root--fullscreen">
      <style>{styles}</style>

      <div className="tma-header">
        <div>
          {nivel === 'bruto' && (
            <button
              type="button"
              className="tma-nav-back"
              onClick={() => setNivel('analisis')}
            >
              <i className="fas fa-arrow-left" />
              Volver al análisis
            </button>
          )}
          <h2 className="tma-header-title">{titulos[nivel]}</h2>
          <p className="tma-header-sub">
            {nivel === 'analisis' && 'Tablas, scores y gráficos procesados. Los datos crudos están un nivel más abajo.'}
            {nivel === 'bruto' && 'Lista completa sin procesar. Edita pisos y notas de administración.'}
          </p>
        </div>
        <div className="tma-search">
          <input type="text" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        {onClose && !hideClose && (
          <button type="button" className="tma-btn-close" onClick={onClose}>
            <i className="fas fa-times" /> Cerrar
          </button>
        )}
      </div>

      {nivel === 'analisis' && (
        <div className="tma-mode-bar">
          {[
            { id: 'rentabilidad', label: 'Rentabilidad', icon: 'fa-coins' },
            { id: 'rentabilidad-real', label: 'Rent. real', icon: 'fa-chart-line' },
            { id: 'cobranza', label: 'Cobranza', icon: 'fa-hand-holding-usd' },
            { id: 'clientes', label: 'Clientes', icon: 'fa-user-check' },
            { id: 'servicios', label: 'Servicios', icon: 'fa-layer-group' },
            { id: 'demoras', label: 'Demoras', icon: 'fa-clock' },
          ].map((t) => (
            <button key={t.id} type="button"
              className={`tma-subtab${subTab === t.id ? ' tma-subtab--active' : ''}`}
              onClick={() => cambiarSubTab(t.id)}>
              <i className={`fas ${t.icon}`} /> {t.label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
            {analisis.total} expedientes · {resumen.pctCobradoGlobal}% cobrado
          </span>
          <button type="button" className="tma-mode-btn tma-mode-btn--ghost" onClick={() => setNivel('bruto')}>
            <i className="fas fa-database" /> Expedientes (datos)
          </button>
        </div>
      )}

      {nivel === 'analisis' && (() => {
        const rent = fmtRentableDestacado(resumen.topRentable);
        const dem = fmtRetrasoDestacado(resumen.peorDemora);
        const cli = resumen.mejorCliente;
        return (
          <div className="tma-insights">
            <div className="tma-insight">
              <div className="tma-insight-label">Más rentable</div>
              <div className="tma-insight-value">{resumen.topRentable?.numero || '—'}</div>
              {rent && (
                <div className="tma-insight-metric">
                  <span className="tma-chip tma-chip--accent">Score {rent.score}/100</span>
                  <span className="tma-chip">{formatCurrency(rent.vendido)}</span>
                  {rent.precioM2 != null && <span className="tma-chip">{formatCurrency(rent.precioM2)}/m²</span>}
                </div>
              )}
            </div>
            <div className="tma-insight">
              <div className="tma-insight-label">Mayor demora</div>
              <div className="tma-insight-value">{resumen.peorDemora?.numero || '—'}</div>
              {dem && (
                <div className="tma-insight-metric">
                  <span className={`tma-chip${dem.tono === 'danger' ? ' tma-chip--danger' : ' tma-chip--ok'}`}>{dem.texto}</span>
                </div>
              )}
            </div>
            <div className="tma-insight">
              <div className="tma-insight-label">Mejor cliente</div>
              <div className="tma-insight-value">{cli?.cliente.nombre || '—'}</div>
              {cli && <div className="tma-insight-detail">Valor {cli.scoreValor} · {cli.cantidad} exp.</div>}
            </div>
          </div>
        );
      })()}

      <div className="tma-scroll">
        {nivel === 'analisis' && (
          <>
            <PanelGraficosAnalisis informe={informe} analisis={analisis} />
            <PanelAnalisis analisis={analisis} subTab={subTab} />
          </>
        )}
        {nivel === 'bruto' && (
          <TablaBrutaAdmin
            datos={datosFiltrados}
            totales={totales}
            onExpedienteActualizado={onExpedienteActualizado}
            busqueda={busqueda}
            setBusqueda={setBusqueda}
          />
        )}
        {datosFiltrados.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
            No hay expedientes con los filtros aplicados
          </div>
        )}
      </div>
    </div>
  );
};

export const VistaMaestraAdminFullscreen = ({ datos, totales, filtros, onExpedienteActualizado, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Análisis administrativo"
      style={{ position: 'fixed', inset: 0, zIndex: 10060, background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <VistaMaestraContenido
        datos={datos}
        totales={totales}
        filtros={filtros}
        onExpedienteActualizado={onExpedienteActualizado}
        onClose={onClose}
      />
    </div>,
    document.body
  );
};

export default VistaMaestraContenido;
